import React from 'react';
import DoctorPicker from '../procedures/DoctorPicker';

/**
 * AdvisedByDoctorField — optional "Advised by doctor" on a Lab & Diagnostic
 * visit (BUSY-VOUCHER R1, OR-42/45/46/48). Fully controlled; no fetches.
 *
 *   - Picker mode (`typedName === null`): the app's searchable DoctorPicker over
 *     `doctors` (the org-wide active roster, any department) plus a
 *     "＋ Type another doctor’s name" row for a doctor who is not on the list.
 *   - Typed mode (`typedName` is a string, '' included): a text input for the
 *     outside doctor, an "Outside doctor" tag and a way back to the list.
 *
 * Nothing is pre-filled or locked. Empty means "use today's automatic value"
 * (resolveAdvisedBy → the advising doctor of a doctor-advised test, else SELF).
 *
 * Props:
 *   doctors    — [{ member_id, first_name, last_name, specialization? }, ...]
 *   doctorId   — picked member_id ('' = none)
 *   typedName  — null (picker mode) | string (typed mode)
 *   onChange({ doctorId, typedName }) — the new pair (one of the two is empty)
 *   isDark     — theme flag (the form's inline-style convention)
 */
export const ADVISED_BY_MAX_LENGTH = 100;

export default function AdvisedByDoctorField({
  doctors = [],
  doctorId = '',
  typedName = null,
  onChange,
  isDark = false,
}) {
  const typing = typedName !== null;

  const labelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '0.5rem',
  };
  const optionalStyle = {
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: 0,
    color: isDark ? '#9ca3af' : '#6b7280',
  };
  const inputStyle = {
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '0.75rem',
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    background: isDark ? 'rgba(255,255,255,0.06)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.9375rem',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const tagStyle = {
    display: 'inline-block',
    marginTop: '0.375rem',
    fontSize: '0.625rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '0.0625rem 0.375rem',
    borderRadius: '9999px',
    background: isDark ? 'rgba(245,158,11,0.25)' : '#fef3c7',
    color: isDark ? '#fcd34d' : '#92400e',
    border: `1px solid ${isDark ? 'rgba(245,158,11,0.4)' : '#fde68a'}`,
  };
  const backStyle = {
    display: 'inline-block',
    marginTop: '0.375rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: isDark ? '#5eead4' : '#0f766e',
    background: isDark ? '#1e293b' : '#fff',
    border: `1px solid ${isDark ? '#64748b' : '#94a3b8'}`,
    borderRadius: '0.5rem',
    padding: '0.125rem 0.5rem',
  };
  const helperStyle = {
    margin: '0.375rem 0 0',
    fontSize: '0.8125rem',
    color: isDark ? '#cbd5e1' : '#475569',
  };

  return (
    <div data-testid="advised-by-field">
      <label style={labelStyle}>
        Advised by doctor <span style={optionalStyle}>(optional)</span>
      </label>
      {typing ? (
        <>
          <input
            type="text"
            aria-label="Advised by doctor"
            value={typedName}
            maxLength={ADVISED_BY_MAX_LENGTH}
            autoFocus
            onChange={(e) => onChange({ doctorId: '', typedName: e.target.value })}
            style={inputStyle}
          />
          <span style={tagStyle}>Outside doctor</span>
          <br />
          <button
            type="button"
            style={backStyle}
            onClick={() => onChange({ doctorId: '', typedName: null })}
          >
            ← Choose from the hospital’s doctors
          </button>
        </>
      ) : (
        <DoctorPicker
          doctors={doctors}
          selectedId={doctorId || null}
          onChange={(id) => onChange({ doctorId: id || '', typedName: null })}
          placeholder="Select a doctor (optional)"
          onTypeOther={() => onChange({ doctorId: '', typedName: '' })}
          typeOtherLabel="＋ Type another doctor’s name"
        />
      )}
      <p style={helperStyle}>Leave empty for SELF (walk-in)</p>
    </div>
  );
}
